import type { MenuProps } from 'antd';
import { useSessionContext } from '@/contexts/session/context';
import { useChatContext } from '@/contexts/chat/context';
import { MediaType } from '@/contexts/chat/scripts/hooks/useQuickVO/types';
import Dialog from '@/components/dialog';
import styles from './index.module.scss';
import MoreMembersIcon from './images/more_members.svg?react';
import VideoIcon from './images/video.svg?react';
import UnVideoIcon from './images/un_video.svg?react';
import MuteIcon from './images/mute.svg?react';
import UnMuteIcon from './images/un_mute.svg?react';
import UnCallIcon from './images/un_call.svg?react';
import SpeakerIcon from './images/speaker.svg?react';
import UnSpeakerIcon from './images/un_speaker.svg?react';
// import AddMembersIcon from './images/add_members.svg?react';
import MoreIcon from './images/more.svg?react';
import ZoomInIcon from './images/zoom_in_min.svg?react';
import ZoomOutIcon from './images/zoom_out.svg?react';
import UnCallMinIcon from './images/un_call_min.svg?react';
import ExpendIcon from './images/expend.svg?react';
import UnExpendIcon from './images/un_expend.svg?react';
import NoDeviceIcon from './images/no_device.svg?react';
import ScreenIcon from './images/screen.svg?react';
import MoreOperate from './components/moreOperate';
import VideoOperate from './components/videoOperate';
import AudioOperate from './components/audioOperate';
import SpeakerOperate from './components/speakerOperate';
import ScreenOperate from './components/screenOperate';
import CallTimer from './components/callTimer';
import AddMembersModal from './components/addMembersModal';
import ParticipantsModal from './components/participantsModal';
import AddParticipantsModal from './components/addParticipantsModal';
import { MoreOperateKey } from './components/moreOperate/type';

const CallOperate = () => {
  const {
    session: { user },
  } = useSessionContext();
  const {
    mediaHangUp,
    chatMediaCall,
    chatMediaQuickVO,
    mediaIsPutThrough,
    mediaIsReady,
    mediaStartTime,
    chatGroup: {
      chatGroupState: {
        state: { membersMap },
      },
    },
  } = useChatContext();
  const {
    roomUsers,
    devices,
    publish,
    stopPublish,
    inactiveTracks,
    getMediaDeviceKind,
    setMediaDeviceKind,
  } = chatMediaQuickVO;

  const normalMemberList = useMemo(
    () =>
      chatMediaCall.chatMediaInfo?.chat?.chat_id
        ? membersMap[chatMediaCall.chatMediaInfo?.chat.chat_id]?.normalMembers
        : [],
    [membersMap, chatMediaCall?.chatMediaInfo?.chat?.chat_id]
  );

  const [openAddMembersModal, setOpenAddMembersModal] = useState(false);
  const [openParticipantsModal, setOpenParticipantsModal] = useState(false);
  const [openAddParticipantsModal, setOpenAddParticipantsModal] = useState(false);

  const roomUser = useMemo(() => roomUsers.find((user) => user.isSelf), [roomUsers]);

  const handleUnCall = async () => {
    await mediaHangUp();
  };

  const handleClickMore: MenuProps['onClick'] = useCallback(
    async ({ key }: { key: string }) => {
      switch (key) {
        case MoreOperateKey.MEET:
          if (roomUser?.callActionMap.screenSharing_video) {
            await stopPublish(MediaType.SCREENSHARING_VIDEO);
          } else {
            Dialog({
              title: <div>Screen sharing on HaloChat is always private</div>,
              confirmText: 'Shared Screen',
              content:
                'The screen enjoys end-to-end encryption protection, and HaloChat will never record its content. No one other than this call can view or listen to the content you share on the screen, and HaloChat is no exception.',
              onConfirm: () => {
                publish(MediaType.SCREENSHARING_VIDEO);
              },
              className: styles.screenDialog,
              confirmClassName: styles.confirmClassName,
              zIndex: 2001,
              centered: true,
            });
          }
          break;
        case MoreOperateKey.MESSAGES:
          break;
        case MoreOperateKey.PARTICIPANTS:
          setOpenParticipantsModal(true);
          break;
        case MoreOperateKey.FULL_SCREEN:
          document.querySelector('#mediaModal')?.requestFullscreen();
          break;
        case MoreOperateKey.SETTING:
          break;
      }
    },
    [roomUser?.callActionMap.screenSharing_video, publish, stopPublish]
  );

  return (
    <>
      <div
        className={cls(
          styles.mediaCallOperate,
          chatMediaCall.mediaModalZoomIn && styles.showMediaCallOperate
        )}
      >
        <div className={cls(styles.left, mediaIsPutThrough && styles.showLeft)}>
          {/* <ShareCallCard onShare={() => setOpenAddMembersModal(true)}> */}
          <div className={styles.members}>
            <div className={styles.names}>
              {chatMediaCall.chatMediaInfo?.callMembers
                ?.filter((item) => item.userId !== user?.userId)
                .map((item) => item.name)
                .join('、')}
            </div>
            <MoreMembersIcon />
          </div>
          {/* </ShareCallCard> */}
          <div className={styles.callStatus}>
            {mediaIsReady ? <CallTimer startTime={mediaStartTime} /> : 'Connecting...'}
          </div>
        </div>
        <div className={styles.center}>
          <div className={styles.item}>
            {roomUser?.callActionMap.microphoneCamera_video ? (
              <VideoIcon
                onClick={async () => {
                  await inactiveTracks(MediaType.MICROPHONECAMERA_VIDEO, false);
                }}
              />
            ) : (
              <UnVideoIcon
                onClick={async () => {
                  await inactiveTracks(MediaType.MICROPHONECAMERA_VIDEO, true);
                }}
              />
            )}
            <div className={styles.expend}>
              <div hidden={!devices.devicesVideoInput.length}>
                <VideoOperate
                  open={chatMediaCall.openMediaModal}
                  callType={chatMediaCall.chatMediaInfo?.payload_type}
                  devicesData={devices.devicesVideoInput}
                  roomUser={roomUser}
                  publish={publish}
                  inactiveTracks={inactiveTracks}
                  getMediaDeviceKind={getMediaDeviceKind}
                  setMediaDeviceKind={setMediaDeviceKind}
                >
                  {roomUser?.callActionMap.microphoneCamera_video ? (
                    <ExpendIcon />
                  ) : (
                    <UnExpendIcon />
                  )}
                </VideoOperate>
              </div>
              <div hidden={!!devices.devicesVideoInput.length}>
                <Tooltip
                  getPopupContainer={(e) => e.parentElement!}
                  title="No found divider"
                >
                  <>
                    <NoDeviceIcon />
                  </>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className={styles.item}>
            {roomUser?.callActionMap.microphoneCamera_audio ? (
              <MuteIcon
                onClick={async () => {
                  await inactiveTracks(MediaType.MICROPHONECAMERA_AUDIO, false);
                }}
              />
            ) : (
              <UnMuteIcon
                onClick={async () => {
                  await inactiveTracks(MediaType.MICROPHONECAMERA_AUDIO, true);
                }}
              />
            )}
            <div className={styles.expend}>
              <div hidden={!devices.devicesAudioInput.length}>
                <AudioOperate
                  open={chatMediaCall.openMediaModal}
                  devicesData={devices.devicesAudioInput}
                  roomUser={roomUser}
                  publish={publish}
                  inactiveTracks={inactiveTracks}
                  getMediaDeviceKind={getMediaDeviceKind}
                  setMediaDeviceKind={setMediaDeviceKind}
                >
                  {roomUser?.callActionMap.microphoneCamera_audio ? (
                    <ExpendIcon />
                  ) : (
                    <UnExpendIcon />
                  )}
                </AudioOperate>
              </div>
              <div hidden={!!devices.devicesAudioInput.length}>
                <Tooltip
                  getPopupContainer={(e) => e.parentElement!}
                  title="No found divider"
                >
                  <>
                    <NoDeviceIcon />
                  </>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className={styles.item}>
            {devices.devicesAudioOutput.length ? <SpeakerIcon /> : <UnSpeakerIcon />}
            <div className={styles.expend}>
              <div hidden={!devices.devicesAudioOutput.length}>
                <SpeakerOperate
                  open={chatMediaCall.openMediaModal}
                  devicesData={devices.devicesAudioOutput}
                  getMediaDeviceKind={getMediaDeviceKind}
                  setMediaDeviceKind={setMediaDeviceKind}
                >
                  <ExpendIcon />
                </SpeakerOperate>
              </div>
              <div hidden={!!devices.devicesAudioOutput.length}>
                <Tooltip
                  getPopupContainer={(e) => e.parentElement!}
                  title="No found divider"
                >
                  <>
                    <NoDeviceIcon />
                  </>
                </Tooltip>
              </div>
            </div>
          </div>
          <UnCallIcon onClick={handleUnCall}></UnCallIcon>
          {/* <AddMembersIcon onClick={() => setOpenAddParticipantsModal(true)} /> */}
          {roomUser?.callActionMap.screenSharing_video && (
            <div className={styles.item}>
              <ScreenIcon />
              <div className={styles.expend}>
                <ScreenOperate stopPublish={stopPublish}>
                  <ExpendIcon />
                </ScreenOperate>
              </div>
            </div>
          )}
          <MoreOperate onClick={handleClickMore}>
            <MoreIcon></MoreIcon>
          </MoreOperate>
        </div>
        <div className={styles.right}>
          <ZoomOutIcon
            onClick={() => {
              chatMediaCall.setMediaModalZoomIn(false);
            }}
          />
        </div>
      </div>
      <div
        className={cls(
          styles.mediaCallOperateZoomOut,
          mediaIsPutThrough && styles.connected,
          chatMediaCall.mediaModalZoomIn && styles.hiddenMediaCallOperateZoomOut
        )}
      >
        <div className={styles.operateBtn}>
          <UnCallMinIcon onClick={handleUnCall} />
          <div className={styles.operateTxt}>Cancel</div>
        </div>
        <div className={styles.operateBtn}>
          <ZoomInIcon
            onClick={() => {
              chatMediaCall.setMediaModalZoomIn(true);
            }}
          />
          <div className={styles.operateTxt}>Full screen</div>
        </div>
      </div>
      <AddParticipantsModal
        normalMemberList={normalMemberList}
        open={openAddParticipantsModal}
        onCancel={() => {
          setOpenAddParticipantsModal(false);
        }}
        onCall={() => {}}
      />
      <AddMembersModal
        normalMemberList={normalMemberList}
        open={openAddMembersModal}
        onCancel={() => {
          setOpenAddMembersModal(false);
        }}
        onSend={() => {}}
      />
      <ParticipantsModal
        normalMemberList={normalMemberList}
        open={openParticipantsModal}
        onCancel={() => {
          setOpenParticipantsModal(false);
        }}
      />
    </>
  );
};
export default CallOperate;
